/**
 * Pure helpers for weekly calendar availability coloring.
 * Plan entries define default green hours; exceptions override for specific datetimes.
 */

const DAY_MAP = { 0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat' };

/**
 * Parse HH:mm to hour number. End times of 00:00 mean midnight (24).
 * @param {string} time
 * @param {boolean} [isEnd]
 * @returns {number}
 */
export const parsePlanHour = (time, isEnd = false) => {
  if (!time || typeof time !== 'string') {
    return NaN;
  }
  if (isEnd && (time === '00:00' || time === '24:00')) {
    return 24;
  }
  const [hour] = time.split(':').map(Number);
  return hour;
};

/**
 * Whether an hour slot falls inside a weekly plan entry for that weekday.
 * @param {Date} day
 * @param {number} hour - 0–23 grid hour
 * @param {Array} availability - { dayOfWeek, startTime, endTime }
 * @returns {boolean}
 */
export const isHourInWeeklyPlan = (day, hour, availability = []) => {
  const dayOfWeek = DAY_MAP[day.getDay()];
  return availability.some(avail => {
    if (avail.dayOfWeek !== dayOfWeek) {
      return false;
    }
    const startHour = parsePlanHour(avail.startTime, false);
    const endHour = parsePlanHour(avail.endTime, true);
    if (Number.isNaN(startHour) || Number.isNaN(endHour)) {
      return false;
    }
    return hour >= startHour && hour < endHour;
  });
};

/**
 * Whether [slotStart, slotEnd) overlaps exception [start, end).
 * @param {Date} slotStart
 * @param {Date} slotEnd
 * @param {Object} exception - Sharetribe-like or local { attributes: { start, end, seats } }
 * @returns {boolean}
 */
export const exceptionOverlapsSlot = (slotStart, slotEnd, exception) => {
  const attrs = exception?.attributes || exception || {};
  if (!attrs.start || !attrs.end) {
    return false;
  }
  const start = new Date(attrs.start);
  const end = new Date(attrs.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }
  return slotStart < end && slotEnd > start;
};

/**
 * Resolve whether a calendar hour cell is available.
 * Exceptions override the weekly plan when they overlap the hour.
 * Empty plan → unavailable (red) unless an "available" exception covers the hour.
 *
 * @param {Date} day
 * @param {number} hour
 * @param {Array} availability
 * @param {Array} [exceptions]
 * @returns {boolean}
 */
export const isTimeSlotAvailable = (day, hour, availability = [], exceptions = []) => {
  const slotStart = new Date(day);
  slotStart.setHours(hour, 0, 0, 0);
  const slotEnd = new Date(day);
  slotEnd.setHours(hour + 1, 0, 0, 0);

  // Later exceptions win if multiple overlap (walk in order; last match wins)
  let exceptionOverride = null;
  for (const ex of exceptions) {
    if (exceptionOverlapsSlot(slotStart, slotEnd, ex)) {
      const seats = ex?.attributes?.seats ?? ex?.seats ?? 0;
      exceptionOverride = seats > 0;
    }
  }
  if (exceptionOverride !== null) {
    return exceptionOverride;
  }

  return isHourInWeeklyPlan(day, hour, availability);
};
